import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import SimpleModal from "components/SimpleModal";
import api from "services/api";
import { useTranslation } from "react-i18next";

const normalizeApiLinkUrl = (url) => {
  if (typeof url !== "string") return url;
  if (url.startsWith("/web2/")) {
    return url.slice("/web2".length);
  }
  return url;
};

const startBrowserDownload = (url, filename) => {
  if (!url || typeof document === "undefined") return;
  const link = document.createElement("a");
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function SignedDownloadButton({ linkUrl, fallbackUrl, label, variant, color }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState({
    filename: "",
    signedUrl: "",
    expiresSeconds: 600,
  });

  const handleCopy = async () => {
    if (!downloadInfo.signedUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(downloadInfo.signedUrl);
      } else {
        const input = document.createElement("textarea");
        input.value = downloadInfo.signedUrl;
        input.setAttribute("readonly", "");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      setError(t("clients.signedDownload.copyError"));
    }
  };

  const requestSignedLink = async () => {
    setOpen(true);
    setLoading(true);
    setError("");
    setCopied(false);
    setDownloadInfo({ filename: "", signedUrl: "", expiresSeconds: 600 });

    try {
      const res = await api.get(normalizeApiLinkUrl(linkUrl));
      const data = res?.data || {};
      if (!data.signed_url) {
        throw new Error("missing_signed_url");
      }
      const nextInfo = {
        filename: data.filename || "",
        signedUrl: data.signed_url,
        expiresSeconds: data.expires_seconds || 600,
      };
      setDownloadInfo(nextInfo);
      startBrowserDownload(nextInfo.signedUrl, nextInfo.filename);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (detail) {
        setError(detail);
      } else if (status === 401) {
        setError(t("clients.signedDownload.loginRequired"));
      } else if (status === 403) {
        setError(t("clients.signedDownload.forbidden"));
      } else if (status === 429) {
        setError(t("clients.signedDownload.rateLimited"));
      } else {
        setError(t("clients.signedDownload.fetchError"));
      }
      if (fallbackUrl) {
        setDownloadInfo((current) => ({ ...current, signedUrl: fallbackUrl }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MKButton variant={variant} color={color} size="small" onClick={requestSignedLink}>
        {label}
      </MKButton>
      <SimpleModal
        open={open}
        title={t("clients.signedDownload.title")}
        showCancel={false}
        showConfirm={false}
        onClose={() => setOpen(false)}
      >
        <Stack spacing={1.5}>
          {loading ? (
            <MKTypography variant="body2" color="text">
              {t("clients.signedDownload.loading")}
            </MKTypography>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!loading && !error && downloadInfo.signedUrl ? (
            <Alert severity="success">{t("clients.signedDownload.started")}</Alert>
          ) : null}
          {downloadInfo.filename ? (
            <MKTypography variant="button" fontWeight="bold" color="text">
              {downloadInfo.filename}
            </MKTypography>
          ) : null}
          {downloadInfo.signedUrl ? (
            <>
              <MKTypography variant="body2" color="text">
                {t("clients.signedDownload.fallbackHint", {
                  minutes: Math.max(1, Math.round(downloadInfo.expiresSeconds / 60)),
                })}
              </MKTypography>
              <MKBox
                component="textarea"
                value={downloadInfo.signedUrl}
                readOnly
                rows={3}
                sx={{
                  width: "100%",
                  resize: "vertical",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: "8px",
                  p: 1,
                  fontSize: "0.8rem",
                  wordBreak: "break-all",
                }}
              />
            </>
          ) : null}
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <MKButton
              variant="gradient"
              color="info"
              size="small"
              component="a"
              href={downloadInfo.signedUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!downloadInfo.signedUrl}
            >
              {t("clients.signedDownload.manualDownload")}
            </MKButton>
            <MKButton
              variant="outlined"
              color="dark"
              size="small"
              onClick={handleCopy}
              disabled={!downloadInfo.signedUrl}
            >
              {copied ? t("clients.signedDownload.copied") : t("clients.signedDownload.copy")}
            </MKButton>
          </Stack>
        </Stack>
      </SimpleModal>
    </>
  );
}

SignedDownloadButton.defaultProps = {
  fallbackUrl: "",
  variant: "gradient",
  color: "info",
};

SignedDownloadButton.propTypes = {
  linkUrl: PropTypes.string.isRequired,
  fallbackUrl: PropTypes.string,
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["contained", "outlined", "gradient", "text"]),
  color: PropTypes.string,
};

export default SignedDownloadButton;
